import { OrderedSet } from 'immutable';
import React, { Component } from 'react';
import Button from '~/UI/Button';
import buttonIconStyles from '~/UI/Button/icon.less';
import Icon from '~/UI/Icon';
import Portal from '~/UI/Portal';
import { Message } from './reducer';

import styles from './styles.less';

interface Props {
	messages: Message[];
	onDismiss?(message: Message): void;
	onMouseEnter?(): void;
	onMouseLeave?(): void;
}

interface State {
	messages: OrderedSet<Message>;
	updatedMessages: OrderedSet<Message>;
}

export default class FlashMessages extends Component<Props, State> {
	isMouseOver = false;

	animationTimeout: NodeJS.Timeout | null = null;

	containerElement: HTMLDivElement | null = null;

	constructor(props: Props) {
		super(props);
		this.state = {
			messages: OrderedSet(props.messages),
			updatedMessages: OrderedSet(),
		};
	}

	componentDidUpdate(prevProps: Props) {
		if (prevProps.messages !== this.props.messages) {
			const messages = OrderedSet(prevProps.messages);
			const nextMessages = OrderedSet(this.props.messages);
			const addedMessages = nextMessages.subtract(messages);
			const removedMessages = messages.subtract(nextMessages);

			this.setState({
				messages: addedMessages.union(messages),
				updatedMessages: addedMessages.union(removedMessages),
			});
		}
		if (!this.state.updatedMessages.isEmpty()) {
			if (this.animationTimeout !== null) {
				clearTimeout(this.animationTimeout);
			}
			this.animationTimeout = setTimeout(
				() =>
					this.setState({
						messages: OrderedSet(this.props.messages),
						updatedMessages: OrderedSet(),
					}),
				200
			);
		}
	}

	componentWillUnmount() {
		if (this.animationTimeout !== null) {
			clearTimeout(this.animationTimeout);
		}
	}

	render() {
		const {
			props: { onDismiss },
			state: { messages, updatedMessages },
		} = this;

		return (
			<Portal>
				<div
					className={styles.container}
					ref={(ref) => (this.containerElement = ref)}
					onFocus={this.handleMouseOver}
					onMouseEnter={this.handleMouseEnter}
					onMouseLeave={this.handleMouseLeave}
					onMouseOver={this.handleMouseOver}
				>
					{messages.toArray().map((message) => (
						<div
							className={
								styles[
									(message.type +
										(updatedMessages.includes(message) ? '' : '-visible')) as keyof (typeof styles)['locals']
								]
							}
							key={message.key}
						>
							<div className={styles.text}>{message.text}</div>
							{onDismiss && message.dismissable ? (
								<div className={styles.dismissButton}>
									<Button styles={buttonIconStyles} onClick={() => onDismiss(message)}>
										<Icon name='close-dark-small' />
									</Button>
								</div>
							) : null}
						</div>
					))}
				</div>
			</Portal>
		);
	}

	handleMouseEnter = () => {
		const { onMouseEnter } = this.props;
		this.isMouseOver = true;

		if (onMouseEnter) {
			onMouseEnter();
		}
	};

	handleMouseOver = () => {
		if (!this.isMouseOver) {
			this.isMouseOver = true;

			const { onMouseEnter } = this.props;
			if (onMouseEnter) {
				onMouseEnter();
			}
		}
	};

	handleMouseLeave = () => {
		const { onMouseLeave } = this.props;
		this.isMouseOver = false;

		if (onMouseLeave) {
			onMouseLeave();
		}
	};
}
